import dayjs from '@/lib/dayjs'

/**
 * Org endpoints send UTC, but written with a space instead of `T` and seconds in the offset
 * (`2026-08-14 10:49:04.32398 +00:00:00`). Browsers parse it today but aren't required to, so convert
 * to ISO first. Same instant, same timezone — only the formatting changes. Valid input is left alone.
 */
export function toIsoTimestamp(value: string): string {
	return (
		value
			.trim()
			// `2026-08-14 10:49:04` -> `2026-08-14T10:49:04`
			.replace(/^(\d{4}-\d{2}-\d{2}) /, '$1T')
			// ` +00:00:00` -> `+00:00`; seconds in the offset are not valid ISO
			.replace(/\s*([+-]\d{2}:\d{2}):\d{2}$/, '$1')
			// ` +00:00` -> `+00:00`
			.replace(/\s+([+-]\d{2}:\d{2})$/, '$1')
			// ` Z` -> `Z`
			.replace(/\s+Z$/, 'Z')
	)
}

/** For columns that need the time, not just the date. */
export const DISPLAY_DATE_TIME_FORMAT = 'MM/DD/YYYY, hh:mm:ss A'

/**
 * Robustly formats a date for display.
 * Handles various backend formats, missing values, and invalid default dates (1970-01-01).
 * Supports string, number, or Date inputs.
 */
export const formatDisplayDate = (
	dateInput: string | number | Date | null | undefined,
	format = 'MMM DD, YYYY',
): string => {
	// Reject nullish, invalid Date objects, and invalid backend date
	if (
		!dateInput ||
		(dateInput instanceof Date && Number.isNaN(dateInput.getTime())) ||
		(typeof dateInput === 'string' && dateInput.startsWith('1970-01-01'))
	) {
		return '--'
	}

	// Normalize first so every engine parses the same string; replaces the old offset-only fixup.
	const normalized = typeof dateInput === 'string' ? toIsoTimestamp(dateInput) : dateInput

	// UTC DayJS parsing (prevents timezone shifts from backend UTC strings)
	let date = dayjs.utc(normalized)

	// Fallback to native Date parsing (sometimes more forgiving for legacy formats)
	if (!date.isValid()) {
		const nativeDate = new Date(normalized as string | number | Date)
		if (!Number.isNaN(nativeDate.getTime())) {
			date = dayjs.utc(nativeDate)
		}
	}

	// Display in local time by default
	return date.isValid() ? date.local().format(format) : String(dateInput ?? '--')
}

/**
 * Formats a duration in milliseconds to a compact, human-readable string.
 * e.g., 5000 -> "5s", 125000 -> "2m 5s", 3665000 -> "1h 1m", etc.
 */
export function formatCompactDuration(ms: number): string {
	if (ms < 0 || Number.isNaN(ms)) {
		return '0s'
	}
	const totalSeconds = Math.floor(ms / 1000)
	const seconds = totalSeconds % 60
	const minutes = Math.floor(totalSeconds / 60)
	const remainingMinutes = minutes % 60
	const hours = Math.floor(minutes / 60)
	const remainingHours = hours % 24
	const days = Math.floor(hours / 24)

	if (days > 0) {
		return `${days}d ${remainingHours}h`
	}
	if (hours > 0) {
		return `${hours}h ${remainingMinutes}m`
	}
	if (remainingMinutes > 0) {
		return `${remainingMinutes}m ${seconds}s`
	}
	return `${seconds}s`
}

/**
 * Safely parses any date/time input (ISO string, UTC millisecond number, or numeric string) into a dayjs.Dayjs object.
 * Automatically scales 10-digit unix timestamps (seconds) to milliseconds for correct modern date parsing.
 */
export function parseSafeDate(input: string | number | Date | null | undefined): dayjs.Dayjs | null {
	if (input === null || input === undefined || input === '') {
		return null
	}

	// Check if it's a number or a numeric string of digits
	const num =
		typeof input === 'number' ? input : typeof input === 'string' && /^\d+$/.test(input) ? Number(input) : null

	if (num !== null && !Number.isNaN(num)) {
		// If it is a 10-digit Unix timestamp (seconds), scale to milliseconds
		if (num > 0 && num < 9999999999) {
			return dayjs(num * 1000)
		}
		return dayjs(num)
	}

	const parsed = dayjs(input)
	return parsed.isValid() ? parsed : null
}

/**
 * Safely parses any backend date string/number/Date (handling +HH:MM:SS timezone formats)
 * into a valid native JavaScript Date object. Returns null if invalid.
 */
export function parseSafeNativeDate(input: string | number | Date | null | undefined): Date | null {
	if (input === null || input === undefined || input === '') {
		return null
	}
	if (input instanceof Date) {
		return Number.isNaN(input.getTime()) ? null : input
	}
	if (typeof input === 'number') {
		const d = new Date(input)
		return Number.isNaN(d.getTime()) ? null : d
	}
	if (typeof input === 'string') {
		if (/^\d+$/.test(input)) {
			const num = Number(input)
			if (num > 0 && num < 9999999999) {
				return new Date(num * 1000)
			}
			return new Date(num)
		}
		let d = new Date(input)
		if (Number.isNaN(d.getTime())) {
			const sanitized = input.replace(/([\+\-]\d{2}:\d{2}):\d{2}$/, '$1')
			d = new Date(sanitized)
		}
		return Number.isNaN(d.getTime()) ? null : d
	}
	return null
}

/**
 * A date condition is always stored as epoch SECONDS (e.g. 1782210218) — matches how the
 * sessions backend stores telemetry timestamps. These helpers convert that number to/from
 * the picker input and the dashboard backend's date string.
 */

/** Turn the stored value (epoch seconds, or an ISO string loaded from a saved filter) into a Date. */
function resolveDateConditionValue(value: unknown): Date | null {
	if (value === null || value === undefined || value === '') {
		return null
	}
	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? null : value
	}
	// Numbers and all-digit strings are epoch SECONDS (the canonical form).
	if (typeof value === 'number' || (typeof value === 'string' && /^\d+$/.test(value))) {
		const d = new Date(Number(value) * 1000)
		return Number.isNaN(d.getTime()) ? null : d
	}
	if (typeof value === 'string') {
		const d = new Date(value)
		return Number.isNaN(d.getTime()) ? null : d
	}
	return null
}

// Zero-pad to 2 digits so date parts stay valid, e.g. month 6 -> "06".
const pad = (n: number): string => String(n).padStart(2, '0')

/** Stored value -> date picker text (local time), e.g. 1782210218 -> "2026-06-23T15:53". Empty if unset. */
export function dateConditionValueToInput(value: unknown, withTime: boolean): string {
	const date = resolveDateConditionValue(value)
	if (!date) {
		return ''
	}
	const ymd = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
	return withTime ? `${ymd}T${pad(date.getHours())}:${pad(date.getMinutes())}` : ymd
}

/**
 * The native date picker only emits strings, so convert the picked string to the stored epoch
 * seconds, e.g. "2026-06-23T15:53" -> 1782210218. Empty if unset/invalid.
 */
export function dateInputToConditionValue(input: string): number | '' {
	if (!input) {
		return ''
	}
	const ms = new Date(input).getTime()
	return Number.isNaN(ms) ? '' : Math.floor(ms / 1000)
}

/**
 * Stored epoch seconds -> the UTC date string the dashboard's platform backend expects.
 * DateTime columns want an RFC3339 timestamp ("2026-06-23T10:23:38.000Z"); Date columns want
 * just "2026-06-23" (pass dateOnly=true). Returns null if the value isn't a usable date.
 */
export function dateConditionValueToBackend(value: unknown, dateOnly: boolean): string | null {
	const date = resolveDateConditionValue(value)
	if (!date) {
		return null
	}
	return dateOnly ? date.toISOString().slice(0, 10) : date.toISOString()
}

/**
 * Age of a dashboard's last completed refresh.
 *
 * Relative while recent, clock time past a minute. It only reaches a clock time once auto-refresh is
 * off or a refresh has stalled, which is when the exact moment matters.
 */
export function formatClockTime(at: number): string {
	return dayjs(at).format('HH:mm:ss')
}

export function formatLastFetched(at: number, now: number = Date.now()): string {
	const seconds = Math.max(0, Math.round((now - at) / 1000))
	if (seconds < 5) {
		return 'Last refreshed just now'
	}
	if (seconds < 60) {
		return `Last refreshed ${seconds}s ago`
	}
	return `Last refreshed at ${formatClockTime(at)}`
}
